import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Save, Send, Trash2, Plus, Newspaper,
  Check, Eye, Clock, Mail, Sparkles, AlertTriangle, FileText, Download,
} from "lucide-react";
import API_BASE from "@/apiBase";

const API = `${API_BASE}/api`;

export default function IndustryOverwatch() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [settings, setSettings] = useState({
    enabled: false,
    send_time_et: "07:00",
    keywords: "",
    subject_template: "Industry Overwatch — {{today_date}}",
    last_sent_at: null,
    last_result: null,
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [compRecipients, setCompRecipients] = useState([]);
  const [compNewEmail, setCompNewEmail] = useState("");
  const [compNewName, setCompNewName] = useState("");
  const [compAdding, setCompAdding] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  // Cardiac Solutions device catalog
  const [products, setProducts] = useState([]);
  const [prodName, setProdName] = useState("");
  const [prodDesc, setProdDesc] = useState("");
  const [prodSaving, setProdSaving] = useState(false);
  const [prodEditingId, setProdEditingId] = useState(null);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 3000); };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, r, h, cr, p] = await Promise.all([
        fetch(`${API}/admin/industry-overwatch/settings`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
        fetch(`${API}/admin/industry-overwatch/recipients`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
        fetch(`${API}/admin/industry-overwatch/history`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
        fetch(`${API}/admin/industry-overwatch/competitive-recipients`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
        fetch(`${API}/admin/industry-overwatch/products`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json()),
      ]);
      setSettings(s);
      setRecipients(r.recipients || []);
      setHistory(h.items || []);
      setCompRecipients(cr.recipients || []);
      setProducts(p.products || []);
    } catch (e) { setErr(e.message || "Failed to load"); }
    setLoading(false);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const saveSettings = async () => {
    setSaving(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Save failed");
      setSettings(d);
      setDirty(false);
      showFlash("Settings saved");
    } catch (e) { setErr(e.message || "Save failed"); }
    setSaving(false);
  };

  const addRecipient = async () => {
    if (!newEmail.trim()) { setErr("Enter an email"); return; }
    setAdding(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/recipients`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: newEmail.trim(), name: newName.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Add failed");
      setRecipients(prev => [...prev, d]);
      setNewEmail(""); setNewName("");
      showFlash("Recipient added");
    } catch (e) { setErr(e.message || "Add failed"); }
    setAdding(false);
  };

  const removeRecipient = async (id) => {
    if (!window.confirm("Remove this recipient?")) return;
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/recipients/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      setRecipients(prev => prev.filter(x => x.id !== id));
      showFlash("Recipient removed");
    } catch (e) { setErr(e.message || "Remove failed"); }
  };

  const addCompRecipient = async () => {
    if (!compNewEmail.trim()) { setErr("Enter an email"); return; }
    setCompAdding(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/competitive-recipients`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: compNewEmail.trim(), name: compNewName.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Add failed");
      setCompRecipients(prev => [...prev, d]);
      setCompNewEmail(""); setCompNewName("");
      showFlash("Competitive recipient added");
    } catch (e) { setErr(e.message || "Add failed"); }
    setCompAdding(false);
  };

  const removeCompRecipient = async (id) => {
    if (!window.confirm("Remove this competitive-analysis recipient?")) return;
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/competitive-recipients/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      setCompRecipients(prev => prev.filter(x => x.id !== id));
      showFlash("Recipient removed");
    } catch (e) { setErr(e.message || "Remove failed"); }
  };

  const saveProduct = async () => {
    if (!prodName.trim() || !prodDesc.trim()) { setErr("Name and description are required"); return; }
    setProdSaving(true); setErr("");
    try {
      const url = prodEditingId
        ? `${API}/admin/industry-overwatch/products/${prodEditingId}`
        : `${API}/admin/industry-overwatch/products`;
      const method = prodEditingId ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: prodName.trim(), description: prodDesc.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      const p = await fetch(`${API}/admin/industry-overwatch/products`, { headers: { Authorization: `Bearer ${token}` } }).then(x => x.json());
      setProducts(p.products || []);
      setProdName(""); setProdDesc(""); setProdEditingId(null);
      showFlash(prodEditingId ? "Product updated" : "Product added");
    } catch (e) { setErr(e.message || "Save failed"); }
    setProdSaving(false);
  };

  const editProduct = (p) => {
    setProdName(p.name); setProdDesc(p.description); setProdEditingId(p.id);
  };

  const cancelEditProduct = () => {
    setProdName(""); setProdDesc(""); setProdEditingId(null);
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Remove this device from the catalog?")) return;
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/products/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      setProducts(prev => prev.filter(x => x.id !== id));
      if (prodEditingId === id) cancelEditProduct();
      showFlash("Product removed");
    } catch (e) { setErr(e.message || "Remove failed"); }
  };

  const downloadCompPdf = async () => {
    setPdfLoading(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/competitive-preview.pdf`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cardiac-Solutions-Competitive-Review-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showFlash("PDF downloaded");
    } catch (e) { setErr(e.message || "PDF failed"); }
    setPdfLoading(false);
  };

  const runPreview = async () => {
    setPreviewing(true); setErr(""); setPreviewData(null);
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/preview`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Preview failed");
      setPreviewData(d);
    } catch (e) { setErr(e.message || "Preview failed"); }
    setPreviewing(false);
  };

  const runNow = async () => {
    if (recipients.length === 0) { setErr("Add at least one recipient first"); return; }
    if (!window.confirm(`Run the search now and email ${recipients.length} recipient(s)?`)) return;
    setRunning(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/run-now`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Run failed");
      showFlash(`Sent ${d.result.sent_count}/${d.result.recipient_count} · ${d.result.article_count} articles`);
      loadAll();
    } catch (e) { setErr(e.message || "Run failed"); }
    setRunning(false);
  };

  const runTest = async () => {
    if (!testTo.trim()) { setErr("Enter a test email"); return; }
    setTesting(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/industry-overwatch/test-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ to: testTo.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Test failed");
      const comp = d.result?.competitive || { generated: 0 };
      const compBit = comp.generated > 0 ? ` · ${comp.generated} competitive review PDF(s)` : "";
      showFlash(`Test search sent to ${testTo} · ${d.result.article_count} article(s)${compBit}`);
    } catch (e) { setErr(e.message || "Test failed"); }
    setTesting(false);
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[rgba(2,6,23,0.95)] border-b border-cyan-500/20 backdrop-blur">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/hub")}
              className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5"
              data-testid="back-to-hub"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-orbitron text-[11px] tracking-widest">HUB</span>
            </button>
            <div className="flex items-center gap-2">
              <Newspaper className="w-4 h-4 text-cyan-400" />
              <div>
                <div className="font-orbitron text-cyan-400 text-sm tracking-widest">INDUSTRY OVERWATCH</div>
                <div className="text-[10px] text-slate-500 tracking-wider">DAILY AED NEWS DIGEST · GPT-5.5 + WEB SEARCH</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] font-orbitron tracking-wider">
            <span className={settings.enabled ? "text-emerald-400" : "text-slate-500"}>
              {settings.enabled ? "AUTOMATION ON" : "AUTOMATION OFF"}
            </span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-500">RECIPIENTS:</span>
            <span className="text-cyan-300">{recipients.length}</span>
          </div>
        </div>
      </div>

      {(flash || err) && (
        <div className="max-w-[1200px] mx-auto px-6 pt-3">
          {flash && (
            <div className="mb-2 px-3 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[11px] font-orbitron tracking-wider flex items-center gap-2" data-testid="flash-msg">
              <Check className="w-3.5 h-3.5" /> {flash}
            </div>
          )}
          {err && (
            <div className="mb-2 px-3 py-2 rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 text-[11px] font-mono flex items-start justify-between gap-3">
              <div className="break-words">{err}</div>
              <button onClick={() => setErr("")} className="text-red-400 hover:text-red-200 shrink-0">×</button>
            </div>
          )}
        </div>
      )}

      <div className="max-w-[1200px] mx-auto px-6 py-4 space-y-4">
        {loading ? (
          <div className="text-slate-500 text-[11px] p-8 text-center">Loading…</div>
        ) : (
          <>
            {/* Settings card */}
            <div className="border border-cyan-500/30 bg-[#0a0f1c] rounded-sm p-5" data-testid="settings-card">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <div className="font-orbitron text-cyan-400 text-xs tracking-widest">DAILY SCAN CONFIGURATION</div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-[10px] font-orbitron text-slate-500 tracking-widest mb-1">DAILY AUTOMATION</label>
                  <button
                    onClick={() => { setSettings(s => ({ ...s, enabled: !s.enabled })); setDirty(true); }}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${settings.enabled ? "bg-emerald-500" : "bg-slate-700"}`}
                    data-testid="enabled-toggle"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${settings.enabled ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                  <div className="text-[9px] text-slate-600 mt-1 font-orbitron tracking-wider">
                    {settings.enabled ? "ON — sends every day at the time below" : "OFF — no automated sends"}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-orbitron text-slate-500 tracking-widest mb-1">SEND TIME (ET)</label>
                  <input
                    type="time"
                    value={settings.send_time_et}
                    onChange={e => { setSettings(s => ({ ...s, send_time_et: e.target.value })); setDirty(true); }}
                    className="w-32 bg-slate-950 border border-slate-700 text-cyan-200 text-sm font-mono px-3 py-2 rounded-sm"
                    data-testid="send-time-input"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-orbitron text-slate-500 tracking-widest mb-1">LAST SENT</label>
                  <div className="text-[11px] font-mono text-slate-300 pt-2">
                    {settings.last_sent_at ? (
                      <div>
                        <div>{new Date(settings.last_sent_at).toLocaleString()}</div>
                        {settings.last_result && (
                          <div className="text-[9px] text-slate-500">
                            {settings.last_result.sent_count}/{settings.last_result.recipient_count} · {settings.last_result.article_count} articles · {settings.last_result.trigger}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-600 italic">never</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-orbitron text-slate-500 tracking-widest mb-1">SEARCH KEYWORDS (comma-separated)</label>
                <textarea
                  value={settings.keywords}
                  onChange={e => { setSettings(s => ({ ...s, keywords: e.target.value })); setDirty(true); }}
                  rows={2}
                  className="w-full bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  placeholder="AED devices, defibrillator news, ..."
                  data-testid="keywords-input"
                />
                <div className="text-[9px] text-slate-600 mt-1">GPT searches the web for articles matching these terms. Prefers the last 7 days.</div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-orbitron text-slate-500 tracking-widest mb-1">EMAIL SUBJECT</label>
                <input
                  value={settings.subject_template}
                  onChange={e => { setSettings(s => ({ ...s, subject_template: e.target.value })); setDirty(true); }}
                  className="w-full bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  placeholder="Industry Overwatch — {{today_date}}"
                  data-testid="subject-input"
                />
                <div className="text-[9px] text-slate-600 mt-1">Use <code className="text-slate-400">{"{{today_date}}"}</code> for today's date.</div>
              </div>

              <div className="mb-4">
                <label className="block text-[10px] font-orbitron text-slate-500 tracking-widest mb-1">CARDIAC SOLUTIONS PROFILE (used for competitive PDF comparisons)</label>
                <textarea
                  value={settings.cs_profile || ""}
                  onChange={e => { setSettings(s => ({ ...s, cs_profile: e.target.value })); setDirty(true); }}
                  rows={5}
                  className="w-full bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  placeholder="Describe Cardiac Solutions devices, capabilities, differentiators…"
                  data-testid="cs-profile-input"
                />
                <div className="text-[9px] text-slate-600 mt-1">
                  When today's scan finds any AED monitor/tracker/competitive product, GPT-5.5 uses this profile to build a polished PDF comparison and emails it (attached) to the competitive-analysis list below.
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={saveSettings}
                  disabled={saving || !dirty}
                  className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 rounded-sm flex items-center gap-1 disabled:opacity-30"
                  data-testid="save-settings-btn"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} SAVE
                </button>
                <button
                  onClick={runPreview}
                  disabled={previewing}
                  className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-sm flex items-center gap-1 disabled:opacity-30"
                  data-testid="preview-btn"
                >
                  {previewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} PREVIEW
                </button>
                <button
                  onClick={runNow}
                  disabled={running || recipients.length === 0}
                  className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 rounded-sm flex items-center gap-1 disabled:opacity-30"
                  data-testid="run-now-btn"
                >
                  {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} RUN NOW
                </button>
              </div>

              {/* Test search — runs the full search pipeline (digest + PDF) and emails a test copy */}
              <div className="mt-4 pt-4 border-t border-slate-800">
                <label className="block text-[10px] font-orbitron text-amber-300 tracking-widest mb-1">
                  TEST SEARCH — runs today's search and emails the digest (plus PDF review if any competitors are found) to a single address. Does not update history.
                </label>
                <div className="flex gap-2">
                  <input
                    value={testTo}
                    onChange={e => setTestTo(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                    data-testid="test-to-input"
                  />
                  <button
                    onClick={runTest}
                    disabled={testing || !testTo.trim()}
                    className="text-[10px] font-orbitron tracking-widest px-4 py-1.5 border border-amber-500/60 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 rounded-sm flex items-center gap-1.5 disabled:opacity-30"
                    data-testid="test-search-btn"
                  >
                    {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} TEST SEARCH
                  </button>
                </div>
              </div>
            </div>

            {/* Recipients card */}
            <div className="border border-cyan-500/30 bg-[#0a0f1c] rounded-sm p-5">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-4 h-4 text-cyan-400" />
                <div className="font-orbitron text-cyan-400 text-xs tracking-widest">RECIPIENT LIST</div>
                <span className="text-[9px] text-slate-500 tracking-wider">· ADMINS ONLY</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Name (optional)"
                  className="w-40 bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  data-testid="new-name-input"
                />
                <input
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="Email address"
                  className="flex-1 min-w-[240px] bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  data-testid="new-email-input"
                  onKeyDown={e => { if (e.key === "Enter") addRecipient(); }}
                />
                <button
                  onClick={addRecipient}
                  disabled={adding || !newEmail.trim()}
                  className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 rounded-sm flex items-center gap-1 disabled:opacity-30"
                  data-testid="add-recipient-btn"
                >
                  {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} ADD
                </button>
              </div>

              {recipients.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-[11px] italic border border-dashed border-slate-800 rounded-sm">
                  No recipients yet. Add someone above to start receiving the daily digest.
                </div>
              ) : (
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-800 text-left font-orbitron text-[9px] text-slate-500 tracking-widest">
                      <th className="py-2 px-2">NAME</th>
                      <th className="py-2 px-2">EMAIL</th>
                      <th className="py-2 px-2">ADDED</th>
                      <th className="py-2 px-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map(r => (
                      <tr key={r.id} className="border-b border-slate-900 hover:bg-slate-900/30" data-testid={`recipient-${r.id}`}>
                        <td className="py-2 px-2 text-slate-200 font-mono">{r.name || "—"}</td>
                        <td className="py-2 px-2 text-cyan-300 font-mono">{r.email}</td>
                        <td className="py-2 px-2 text-slate-500 font-mono text-[10px]">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="py-2 px-2 text-right">
                          <button
                            onClick={() => removeRecipient(r.id)}
                            className="text-[9px] font-orbitron tracking-widest px-2 py-1 border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm flex items-center gap-1"
                            data-testid={`remove-${r.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> REMOVE
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Competitive analysis card */}
            <div className="border border-amber-500/30 bg-[#150e08] rounded-sm p-5" data-testid="competitive-card">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-400" />
                  <div className="font-orbitron text-amber-300 text-xs tracking-widest">COMPETITIVE PRODUCT REVIEWS (PDF)</div>
                </div>
                <button
                  onClick={downloadCompPdf}
                  disabled={pdfLoading}
                  className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm flex items-center gap-1 disabled:opacity-30"
                  data-testid="download-pdf-btn"
                >
                  {pdfLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} PREVIEW PDF
                </button>
              </div>
              <div className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                When today's scan surfaces any AED monitoring device, tracker, or competitor product, GPT-5.5 generates a polished side-by-side comparison against the Cardiac Solutions platform and emails it as a PDF attachment to the addresses below. Uses the Cardiac Solutions profile from the settings above.
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                <input
                  value={compNewName}
                  onChange={e => setCompNewName(e.target.value)}
                  placeholder="Name (optional)"
                  className="w-40 bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  data-testid="comp-new-name-input"
                />
                <input
                  value={compNewEmail}
                  onChange={e => setCompNewEmail(e.target.value)}
                  placeholder="Email address"
                  className="flex-1 min-w-[240px] bg-slate-950 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  data-testid="comp-new-email-input"
                  onKeyDown={e => { if (e.key === "Enter") addCompRecipient(); }}
                />
                <button
                  onClick={addCompRecipient}
                  disabled={compAdding || !compNewEmail.trim()}
                  className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm flex items-center gap-1 disabled:opacity-30"
                  data-testid="comp-add-recipient-btn"
                >
                  {compAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} ADD
                </button>
              </div>

              {compRecipients.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-[11px] italic border border-dashed border-slate-800 rounded-sm">
                  No competitive-analysis recipients yet. Add someone above to receive PDF comparisons when competitors appear.
                </div>
              ) : (
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-800 text-left font-orbitron text-[9px] text-slate-500 tracking-widest">
                      <th className="py-2 px-2">NAME</th>
                      <th className="py-2 px-2">EMAIL</th>
                      <th className="py-2 px-2">ADDED</th>
                      <th className="py-2 px-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {compRecipients.map(r => (
                      <tr key={r.id} className="border-b border-slate-900 hover:bg-slate-900/30" data-testid={`comp-recipient-${r.id}`}>
                        <td className="py-2 px-2 text-slate-200 font-mono">{r.name || "—"}</td>
                        <td className="py-2 px-2 text-amber-300 font-mono">{r.email}</td>
                        <td className="py-2 px-2 text-slate-500 font-mono text-[10px]">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="py-2 px-2 text-right">
                          <button
                            onClick={() => removeCompRecipient(r.id)}
                            className="text-[9px] font-orbitron tracking-widest px-2 py-1 border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm flex items-center gap-1"
                            data-testid={`comp-remove-${r.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> REMOVE
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Cardiac Solutions device catalog — comparison context */}
            <div className="border border-cyan-500/30 bg-[#0a0f1c] rounded-sm p-5" data-testid="cs-devices-card">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <div className="font-orbitron text-cyan-400 text-xs tracking-widest">CARDIAC SOLUTIONS DEVICES</div>
                <span className="text-[9px] text-slate-500 tracking-wider">· PRODUCT COMPARISON CONTEXT</span>
              </div>
              <div className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                Add each Cardiac Solutions device that GPT should reference when writing side-by-side comparisons against new competitor product releases. Give it a clear name and 2-4 sentences describing what it does and its differentiators.
              </div>

              <div className="border border-slate-800 rounded-sm bg-slate-950 p-3 mb-3 space-y-2">
                <input
                  value={prodName}
                  onChange={e => setProdName(e.target.value)}
                  placeholder="Device name (e.g. Sentinel Cellular Monitor)"
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  data-testid="cs-product-name"
                />
                <textarea
                  value={prodDesc}
                  onChange={e => setProdDesc(e.target.value)}
                  placeholder="Description — capabilities, sensors, differentiators…"
                  rows={4}
                  className="w-full bg-slate-900 border border-slate-700 text-slate-200 text-sm font-mono px-3 py-2 rounded-sm"
                  data-testid="cs-product-desc"
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveProduct}
                    disabled={prodSaving || !prodName.trim() || !prodDesc.trim()}
                    className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 rounded-sm flex items-center gap-1 disabled:opacity-30"
                    data-testid="cs-product-save"
                  >
                    {prodSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : (prodEditingId ? <Save className="w-3 h-3" /> : <Plus className="w-3 h-3" />)}
                    {prodEditingId ? "UPDATE DEVICE" : "ADD DEVICE"}
                  </button>
                  {prodEditingId && (
                    <button
                      onClick={cancelEditProduct}
                      className="text-[10px] font-orbitron tracking-widest px-3 py-1.5 border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-sm"
                      data-testid="cs-product-cancel"
                    >
                      CANCEL
                    </button>
                  )}
                </div>
              </div>

              {products.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-[11px] italic border border-dashed border-slate-800 rounded-sm">
                  No devices in the catalog yet. Add each Cardiac Solutions product so competitive comparisons are accurate.
                </div>
              ) : (
                <div className="space-y-2">
                  {products.map(p => (
                    <div key={p.id} className="border border-slate-800 rounded-sm p-3 bg-slate-950/40" data-testid={`cs-product-${p.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-orbitron text-cyan-300 text-[12px] tracking-wider mb-1">{p.name}</div>
                          <div className="text-[11px] text-slate-300 font-mono whitespace-pre-wrap leading-relaxed">{p.description}</div>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            onClick={() => editProduct(p)}
                            className="text-[9px] font-orbitron tracking-widest px-2 py-1 border border-slate-600 text-slate-300 hover:bg-slate-800 rounded-sm"
                            data-testid={`cs-product-edit-${p.id}`}
                          >
                            EDIT
                          </button>
                          <button
                            onClick={() => removeProduct(p.id)}
                            className="text-[9px] font-orbitron tracking-widest px-2 py-1 border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded-sm flex items-center gap-1"
                            data-testid={`cs-product-remove-${p.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> REMOVE
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* History */}
            <div className="border border-slate-800 rounded-sm bg-[#0a0f1c] p-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-slate-500" />
                <div className="font-orbitron text-slate-400 text-xs tracking-widest">RECENT DIGESTS</div>
              </div>
              {history.length === 0 ? (
                <div className="text-slate-500 text-[11px] italic">No digests sent yet.</div>
              ) : (
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-800 text-left font-orbitron text-[9px] text-slate-500 tracking-widest">
                      <th className="py-2 px-2">DATE</th>
                      <th className="py-2 px-2">SUBJECT</th>
                      <th className="py-2 px-2 w-20 text-right">ARTICLES</th>
                      <th className="py-2 px-2 w-20 text-right">SENT</th>
                      <th className="py-2 px-2 w-24">TRIGGER</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} className="border-b border-slate-900 hover:bg-slate-900/30">
                        <td className="py-2 px-2 text-slate-300 font-mono">{new Date(h.date).toLocaleString()}</td>
                        <td className="py-2 px-2 text-slate-200 font-mono">{h.subject}</td>
                        <td className="py-2 px-2 text-cyan-300 font-mono text-right">{h.articles?.length || 0}</td>
                        <td className="py-2 px-2 text-emerald-300 font-mono text-right">{h.sent_count}/{h.recipient_count}</td>
                        <td className="py-2 px-2 text-slate-500 font-mono text-[10px]">{h.trigger}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {/* Preview modal */}
      {previewData && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setPreviewData(null)}>
          <div className="bg-[#0a0f1c] border border-cyan-500/30 rounded-sm w-full max-w-[820px] max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="border-b border-cyan-500/15 px-5 py-3 flex items-center justify-between">
              <div>
                <div className="font-orbitron text-sm text-cyan-400 tracking-widest">PREVIEW · {previewData.article_count} ARTICLE(S)</div>
                <div className="text-[10px] text-slate-500 tracking-wider">{previewData.subject}</div>
              </div>
              <button onClick={() => setPreviewData(null)} className="text-slate-500 hover:text-white text-lg">×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {previewData.article_count === 0 && (
                <div className="mb-4 px-3 py-2 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 text-[11px] font-orbitron tracking-wider flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" /> Search returned 0 articles. Adjust keywords in the settings above.
                </div>
              )}
              <div className="bg-white text-black p-3 rounded" dangerouslySetInnerHTML={{ __html: previewData.html }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
